import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
    signal,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { FuseConfigService, Scheme } from '@fuse/services/config';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { SignalRService, ConnectionState } from 'app/core/services/signalr.service';
import { AuthState } from 'app/core/state/auth.state';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatIconModule,
        NgClass,
        MatDividerModule,
        MatSnackBarModule,
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    static ngAcceptInputType_showAvatar: BooleanInput;

    @ViewChild('userOrigin') private _userOrigin: MatButton;
    @ViewChild('userPanel') private _userPanel: TemplateRef<any>;

    @Input() showAvatar: boolean = true;
    user: User;
    scheme: Scheme = 'dark';

    // SignalR connection state
    signalRState = signal<ConnectionState>('Disconnected');
    isTestingConnection = signal<boolean>(false);

    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private _authState = inject(AuthState);
    private _navigationService = inject(NavigationService);

    /** Whether the user has admin access (controls toggle visibility) */
    hasAdminAccess = this._authState.hasAdminAccess;
    /** Current nav mode signal */
    navMode = this._authState.navMode;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _router: Router,
        private _userService: UserService,
        private _fuseConfigService: FuseConfigService,
        private _signalRService: SignalRService,
        private _snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to config changes for scheme
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {
                this.scheme = config.scheme;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to SignalR connection state
        this._signalRService.connectionState$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((state) => {
                this.signalRState.set(state);
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    openPanel(): void {
        if (!this._userPanel || !this._userOrigin) {
            return;
        }

        if (!this._overlayRef) {
            this._createOverlay();
        }

        this._overlayRef.attach(
            new TemplatePortal(this._userPanel, this._viewContainerRef)
        );
    }

    closePanel(): void {
        this._overlayRef.detach();
    }

    updateUserStatus(status: string): void {
        if (!this.user) {
            return;
        }

        this._userService
            .update({
                ...this.user,
                status,
            })
            .subscribe();

        this.closePanel();
    }

    /**
     * Test SignalR connection and reconnect if disconnected
     */
    async testSignalRConnection(): Promise<void> {
        this.isTestingConnection.set(true);
        const currentState = this.signalRState();

        try {
            if (currentState === 'Connected') {
                // Already connected - just show confirmation
                this._snackBar.open('SignalR is connected', 'OK', {
                    duration: 3000,
                    panelClass: ['bg-green-600', 'text-white']
                });
            } else {
                // Try to reconnect
                this._snackBar.open('Reconnecting to SignalR...', '', { duration: 2000 });

                // Stop existing connection first
                await this._signalRService.stopConnection();

                // Start new connection with user ID
                const userId = this.user?.id || this.user?.email;
                await this._signalRService.startConnection(userId);

                // Check if connected
                const newState = this._signalRService.connectionState();
                if (newState === 'Connected') {
                    this._snackBar.open('SignalR reconnected successfully!', 'OK', {
                        duration: 3000,
                        panelClass: ['bg-green-600', 'text-white']
                    });
                } else {
                    this._snackBar.open(`SignalR connection failed: ${newState}`, 'Retry', {
                        duration: 5000,
                        panelClass: ['bg-red-600', 'text-white']
                    });
                }
            }
        } catch (error) {
            console.error('SignalR test/reconnect failed:', error);
            this._snackBar.open('SignalR connection failed. Check console for details.', 'OK', {
                duration: 5000,
                panelClass: ['bg-red-600', 'text-white']
            });
        } finally {
            this.isTestingConnection.set(false);
            this._changeDetectorRef.markForCheck();
        }
    }

    /**
     * Get display text for SignalR connection state
     */
    getSignalRStatusText(): string {
        const state = this.signalRState();
        switch (state) {
            case 'Connected': return 'Connected';
            case 'Connecting': return 'Connecting...';
            case 'Reconnecting': return 'Reconnecting...';
            case 'Disconnected': return 'Disconnected';
            default: return 'Unknown';
        }
    }

    /**
     * Get icon for SignalR connection state
     */
    getSignalRStatusIcon(): string {
        const state = this.signalRState();
        switch (state) {
            case 'Connected': return 'heroicons_outline:signal';
            case 'Connecting':
            case 'Reconnecting': return 'heroicons_outline:arrow-path';
            case 'Disconnected': return 'heroicons_outline:signal-slash';
            default: return 'heroicons_outline:question-mark-circle';
        }
    }

    signOut(): void {
        this.closePanel();
        this._router.navigate(['/sign-out']);
    }

    toggleScheme(): void {
        this.scheme = this.scheme === 'dark' ? 'light' : 'dark';
        this._fuseConfigService.config = { scheme: this.scheme };
    }

    toggleNavMode(): void {
        this._authState.toggleNavMode();
        this._navigationService.refreshNavigation();
        const newMode = this._authState.navMode();
        if (newMode === 'user') {
            this._router.navigate(['/main-dashboard']);
        } else {
            this._router.navigate(['/admin/dashboard']);
        }
        this.closePanel();
    }

    private _createOverlay(): void {
    this._overlayRef = this._overlay.create({
        hasBackdrop: true,
        backdropClass: 'fuse-backdrop-on-mobile',
        scrollStrategy: this._overlay.scrollStrategies.block(),
        positionStrategy: this._overlay
            .position()
            .flexibleConnectedTo(this._userOrigin._elementRef.nativeElement)
            .withLockedPosition(true)
            .withPush(true)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                },
                {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'top',
                },
                {
                    originX: 'end',
                    originY: 'top',
                    overlayX: 'end',
                    overlayY: 'bottom',
                },
            ]),
    });

    this._overlayRef.backdropClick().subscribe(() => {
        this._overlayRef.detach();
    });
}
}